import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Search, 
  CheckCircle,
  Clock,
  Edit,
  Trash2,
  Eye,
  X,
  RefreshCw,
  AlertCircle,
  AlertTriangle,
  Calendar,
  Shield,
  Download,
  Upload
} from 'lucide-react';
import EnqueteDetail from './EnqueteDetail';
import { useEnquetes } from '../hooks/useEnquetes';
import { InputValidator, SecureErrorHandler, FormValidator } from '../utils/security';
import { Client } from '../services/crudService';
import { API_ENDPOINTS } from '../config/api';
import ExpertModal from './modals/ExpertModal';

interface EnqueteManagerProps {
  onEnqueteSelect?: (enquete: any) => void;
}

const EnqueteManager: React.FC<EnqueteManagerProps> = ({ onEnqueteSelect }) => {
  const {
    enquetes,
    loading,
    error,
    stats,
    statsLoading,
    createEnquete,
    updateEnquete,
    deleteEnquete,
    refreshEnquetes,
    setSearch,
    setStatut,
    setPriorite,
    setSortBy,
    setSortOrder
  } = useEnquetes();

  const [isAddingEnquete, setIsAddingEnquete] = useState(false);
  const [isEditingEnquete, setIsEditingEnquete] = useState<string | null>(null);
  const [selectedEnqueteForDetail, setSelectedEnqueteForDetail] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatut, setFilterStatut] = useState<string>('tous');
  const [filterPriorite, setFilterPriorite] = useState<string>('tous');
  const [sortBy, setSortByState] = useState<'date' | 'priorite' | 'statut' | 'titre'>('date');
  const [sortOrder, setSortOrderState] = useState<'asc' | 'desc'>('desc');
  const [isExpertModalOpen, setIsExpertModalOpen] = useState(false);

  const [formData, setFormData] = useState({
    titre: '',
    description: '',
    objectifs: '',
    elementsFournis: '',
    statut: 'en_attente' as const,
    priorite: 'moyenne' as const,
    client: '',
    budget: 0,
    assigne: 'Détective Privé',
    tags: [] as string[]
  });

  const [newTag, setNewTag] = useState('');
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [clients, setClients] = useState<Client[]>([]);
  const [loadingClients, setLoadingClients] = useState(false);

  // Synchroniser les états locaux avec le hook
  useEffect(() => {
    setSearch(searchTerm);
  }, [searchTerm, setSearch]);

  useEffect(() => {
    setStatut(filterStatut);
  }, [filterStatut, setStatut]);

  useEffect(() => {
    setPriorite(filterPriorite);
  }, [filterPriorite, setPriorite]);

  useEffect(() => {
    setSortBy(sortBy);
  }, [sortBy, setSortBy]);

  useEffect(() => {
    setSortOrder(sortOrder);
  }, [sortOrder, setSortOrder]);

  // Charger les clients au montage
  useEffect(() => {
    const loadClients = async () => {
      try {
        setLoadingClients(true);
        const token = localStorage.getItem('accessToken');
        if (!token) {
          if (process.env.NODE_ENV !== 'production') {
            console.error('Token manquant pour charger les clients');
          }
          return;
        }

        const response = await fetch(API_ENDPOINTS.CLIENTS, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          setClients(data.data || []);
        } else {
          if (process.env.NODE_ENV !== 'production') {
            console.error('Erreur lors du chargement des clients:', response.status);
          }
        }
      } catch (error) {
        if (process.env.NODE_ENV !== 'production') {
          console.error('Erreur lors du chargement des clients:', error);
        }
      } finally {
        setLoadingClients(false);
      }
    };

    loadClients();
  }, []);

  // Forcer un rechargement des enquêtes et stats au montage pour synchroniser les données
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      console.log('🔄 EnqueteManager mounted - refreshing data');
    }
    refreshEnquetes();
  }, [refreshEnquetes]);

  // Validation du formulaire
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!FormValidator.validateRequired(formData.titre)) {
      errors.titre = 'Le titre est requis';
    } else if (!FormValidator.validateMinLength(formData.titre, 3)) {
      errors.titre = 'Le titre doit contenir au moins 3 caractères';
    }

    if (!FormValidator.validateRequired(formData.description)) {
      errors.description = 'La description est requise';
    } else if (!FormValidator.validateMinLength(formData.description, 10)) {
      errors.description = 'La description doit contenir au moins 10 caractères';
    }

    if (!FormValidator.validateRequired(formData.objectifs)) {
      errors.objectifs = 'Les objectifs sont requis';
    } else if (!FormValidator.validateMinLength(formData.objectifs, 10)) {
      errors.objectifs = 'Les objectifs doivent contenir au moins 10 caractères';
    }

    if (!FormValidator.validateRequired(formData.elementsFournis)) {
      errors.elementsFournis = 'Les éléments fournis sont requis';
    } else if (!FormValidator.validateMinLength(formData.elementsFournis, 10)) {
      errors.elementsFournis = 'Les éléments fournis doivent contenir au moins 10 caractères';
    }

    if (!FormValidator.validateRequired(formData.client)) {
      errors.client = 'Le client est requis';
    } else if (!clients.find(c => c.id === formData.client)) {
      errors.client = 'Veuillez sélectionner un client valide';
    }

    if (formData.budget < 0) {
      errors.budget = 'Le budget ne peut pas être négatif';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      if (isEditingEnquete) {
        await updateEnquete(isEditingEnquete, formData);
      } else {
        await createEnquete(formData);
      }
      resetForm();
    } catch (error) {
      SecureErrorHandler.handleError(error, 'EnqueteManager.handleSubmit');
      // L'erreur sera gérée par le hook useEnquetes
    }
  };

  const handleEdit = (enquete: any) => {
    setFormData({
      titre: enquete.titre,
      description: enquete.description,
      objectifs: enquete.objectifs || '',
      elementsFournis: enquete.elements_fournis || enquete.elementsFournis || '',
      statut: enquete.statut,
      priorite: enquete.priorite,
      client: enquete.client?.id || enquete.client_id || '',
      budget: enquete.budget,
      assigne: enquete.assigne_a || enquete.assigne || '',
      tags: enquete.tags || []
    });
    setIsEditingEnquete(enquete.id);
    setIsAddingEnquete(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette enquête ?')) {
      try {
        await deleteEnquete(id);
      } catch (error) {
        SecureErrorHandler.handleError(error, 'EnqueteManager.handleDelete');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      titre: '',
      description: '',
      objectifs: '',
      elementsFournis: '',
      statut: 'en_attente',
      priorite: 'moyenne',
      client: clients.length > 0 ? clients[0].id : '', // Sélectionner le premier client par défaut
      budget: 0,
      assigne: 'Détective Privé',
      tags: []
    });
    setIsAddingEnquete(false);
    setIsEditingEnquete(null);
    setNewTag('');
    setFormErrors({});
  };

  const addTag = () => {
    const sanitizedTag = InputValidator.sanitizeString(newTag);
    if (sanitizedTag && !formData.tags.includes(sanitizedTag)) {
      setFormData(prev => ({ ...prev, tags: [...prev.tags, sanitizedTag] }));
      setNewTag('');
    }
  };

  const removeTag = (tag: string) => {
    setFormData(prev => ({ ...prev, tags: prev.tags.filter(t => t !== tag) }));
  };

  const getStatutColor = (statut: string) => {
    switch (statut) {
      case 'en_cours': return 'bg-blue-600 text-white';
      case 'terminee': return 'bg-green-600 text-white';
      case 'en_attente': return 'bg-yellow-600 text-white';
      case 'archivee': return 'bg-gray-600 text-white';
      default: return 'bg-gray-600 text-white';
    }
  };

  const getPrioriteColor = (priorite: string) => {
    switch (priorite) {
      case 'haute': return 'bg-red-600 text-white';
      case 'moyenne': return 'bg-yellow-600 text-white';
      case 'basse': return 'bg-green-600 text-white';
      default: return 'bg-gray-600 text-white';
    }
  };



  const handleViewDetails = (enqueteId: string) => {
    setSelectedEnqueteForDetail(enqueteId);
  };

  const handleCloseDetail = () => {
    setSelectedEnqueteForDetail(null);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-white">Gestion des Enquêtes</h2>
        <div className="flex items-center space-x-2">
          <button
            onClick={refreshEnquetes}
            disabled={loading}
            className="flex items-center space-x-2 px-3 py-2 bg-osint-light-gray hover:bg-gray-600 disabled:opacity-50 rounded-lg text-white text-sm transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span>Actualiser</span>
          </button>
          <button
            onClick={() => setIsAddingEnquete(true)}
            className="osint-button px-4 py-2 rounded-lg text-white font-medium flex items-center space-x-2 bg-osint-orange hover:bg-orange-600"
          >
            <Plus className="w-5 h-5" />
            <span>Nouvelle Enquête</span>
          </button>
          <button
            onClick={() => setIsExpertModalOpen(true)}
            className="osint-button px-4 py-2 rounded-lg text-white font-medium flex items-center space-x-2 bg-osint-orange hover:bg-purple-700"
          >
            <Shield className="w-5 h-5" />
            <span>Faire appel à un expert</span>
          </button>
        </div>
      </div>

      {/* Message d'erreur */}
      {error && (
        <div className="osint-card p-4 rounded-lg border border-red-500/50 bg-red-500/10">
          <div className="flex items-center space-x-2">
            <AlertCircle className="w-5 h-5 text-red-400" />
            <span className="text-red-400">{SecureErrorHandler.sanitizeErrorMessage({ status: 500 })}</span>
          </div>
        </div>
      )}

      {/* Statistiques */}
      {!statsLoading && stats && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="osint-card p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">Total Enquêtes</p>
                <p className="text-2xl font-bold text-white">{stats.total}</p>
                <p className="text-xs text-gray-500">Affichées: {enquetes.length}</p>
              </div>
              <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center">
                <Eye className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>

          <div className="osint-card p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">En Cours</p>
                <p className="text-2xl font-bold text-white">{stats.enCours}</p>
              </div>
              <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center">
                <Clock className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>

          <div className="osint-card p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">Terminées</p>
                <p className="text-2xl font-bold text-white">{stats.terminees}</p>
              </div>
              <div className="w-12 h-12 bg-green-600 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>

          <div className="osint-card p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">En Attente</p>
                <p className="text-2xl font-bold text-white">{stats.enAttente}</p>
              </div>
              <div className="w-12 h-12 bg-yellow-600 rounded-lg flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>

          <div className="osint-card p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">Priorité Haute</p>
                <p className="text-2xl font-bold text-white">{stats.hautePriorite}</p>
              </div>
              <div className="w-12 h-12 bg-red-600 rounded-lg flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filtres et recherche */}
      <div className="osint-card p-6 rounded-lg">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Rechercher une enquête..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(InputValidator.sanitizeString(e.target.value))}
                className="osint-input w-full pl-10 pr-4 py-2 rounded-lg text-white placeholder-gray-400"
              />
            </div>
          </div>

          <div className="flex gap-2">
            <select
              value={filterStatut}
              onChange={(e) => setFilterStatut(e.target.value)}
              className="osint-input px-3 py-2 rounded-lg text-white"
            >
              <option value="tous">Tous les statuts</option>
              <option value="en_cours">En cours</option>
              <option value="terminee">Terminée</option>
              <option value="en_attente">En attente</option>
              <option value="archivee">Archivée</option>
            </select>

            <select
              value={filterPriorite}
              onChange={(e) => setFilterPriorite(e.target.value)}
              className="osint-input px-3 py-2 rounded-lg text-white"
            >
              <option value="tous">Toutes les priorités</option>
              <option value="haute">Haute</option>
              <option value="moyenne">Moyenne</option>
              <option value="basse">Basse</option>
            </select>

            <select
              value={`${sortBy}-${sortOrder}`}
              onChange={(e) => {
                const [field, order] = e.target.value.split('-');
                setSortByState(field as any);
                setSortOrderState(order as any);
              }}
              className="osint-input px-3 py-2 rounded-lg text-white"
            >
              <option value="date-desc">Plus récentes</option>
              <option value="date-asc">Plus anciennes</option>
              <option value="priorite-desc">Priorité décroissante</option>
              <option value="priorite-asc">Priorité croissante</option>
              <option value="titre-asc">Titre A-Z</option>
              <option value="titre-desc">Titre Z-A</option>
            </select>
          </div>
        </div>
      </div>

      {/* Liste des enquêtes */}
      <div className="osint-card rounded-lg">
        <div className="p-6 border-b border-osint-light-gray flex items-center justify-between">
          <h3 className="text-lg font-semibold text-white flex items-center">Enquêtes ({loading ? '...' : enquetes.length})</h3>
          <div className="flex items-center gap-2">
            <button
              onClick={() => {/* TODO: importer */}}
              className="osint-button px-3 py-2 rounded-lg text-white text-sm flex items-center space-x-2 bg-osint-light-gray hover:bg-osint-orange transition-colors"
              title="Importer des enquêtes"
            >
              <Upload className="w-4 h-4" />
              <span className="hidden sm:inline">Importer</span>
            </button>
            <button
              onClick={() => {/* TODO: exporter */}}
              className="osint-button px-3 py-2 rounded-lg text-white text-sm flex items-center space-x-2 bg-osint-light-gray hover:bg-osint-orange transition-colors"
              title="Exporter les enquêtes"
            >
              <Download className="w-4 h-4" />
              <span className="hidden sm:inline">Exporter</span>
            </button>
          </div>
        </div>

        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-8 h-8 text-osint-orange animate-spin" />
              <span className="ml-2 text-white">Chargement...</span>
            </div>
          ) : enquetes.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-400">Aucune enquête trouvée</p>
            </div>
          ) : (
            <div className="space-y-4">
              {enquetes.map((enquete) => (
                <div 
                  key={enquete.id} 
                  className="border border-osint-light-gray rounded-lg p-4 hover:bg-osint-light-gray transition-colors cursor-pointer"
                  onClick={() => handleViewDetails(enquete.id)}
                  title="Cliquer pour voir les détails de cette enquête"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h4 className="text-white font-medium">{enquete.titre}</h4>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatutColor(enquete.statut)}`}>
                          {enquete.statut.replace('_', ' ')}
                        </span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPrioriteColor(enquete.priorite)}`}>
                          {enquete.priorite}
                        </span>
                        {(enquete as any).access_type === 'shared' && (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-600/20 text-blue-400 border border-blue-500/30">
                            Partagée
                          </span>
                        )}
                      </div>
                      {(enquete as any).access_type === 'shared' && (enquete as any).shared_by_name && (
                        <div className="flex items-center space-x-2 mb-2">
                          <div className="w-1 h-1 bg-blue-400 rounded-full"></div>
                          <span className="text-xs text-blue-400">
                            Partagée par {(enquete as any).shared_by_name}
                            {(enquete as any).shared_at && (
                              <span className="text-gray-500 ml-1">
                                le {new Date((enquete as any).shared_at).toLocaleDateString()}
                              </span>
                            )}
                          </span>
                        </div>
                      )}
                      <p className="text-gray-400 text-sm mb-2">{enquete.description}</p>
                      <div className="flex items-center space-x-4 text-xs text-gray-500">
                        <span className="flex items-center space-x-1">
                          <Calendar className="w-3 h-3" />
                          <span>{new Date((enquete as any).date_creation || enquete.dateCreation).toLocaleDateString()}</span>
                        </span>
                        <span>•</span>
                        <span>{(enquete.client as any)?.nom || enquete.client}</span>
                        <span>•</span>
                        <span>{enquete.budget}€</span>
                      </div>
                      {enquete.tags.length > 0 && (
                        <div className="flex items-center space-x-2 mt-2">
                          {enquete.tags.slice(0, 3).map(tag => (
                            <span key={tag} className="px-2 py-1 bg-osint-orange/20 text-osint-orange text-xs rounded">
                              {tag}
                            </span>
                          ))}
                          {enquete.tags.length > 3 && (
                            <span className="text-xs text-gray-400">+{enquete.tags.length - 3}</span>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewDetails(enquete.id);
                        }}
                        className="flex items-center space-x-2 px-3 py-2 bg-osint-orange hover:bg-orange-600 rounded-lg text-white text-sm transition-colors"
                        title="Voir les détails de cette enquête"
                      >
                        <Eye className="w-4 h-4" />
                        <span>Voir détails</span>
                      </button>
                      {(enquete as any).access_type !== 'shared' && (
                        <>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEdit(enquete);
                            }}
                            className="flex items-center space-x-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-white text-sm transition-colors"
                            title="Modifier cette enquête"
                          >
                            <Edit className="w-4 h-4" />
                            <span>Modifier</span>
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(enquete.id);
                            }}
                            className="flex items-center space-x-2 px-3 py-2 bg-red-600 hover:bg-red-700 rounded-lg text-white text-sm transition-colors"
                            title="Supprimer cette enquête"
                          >
                            <Trash2 className="w-4 h-4" />
                            <span>Supprimer</span>
                          </button>
                        </>
                      )}
                      {(enquete as any).access_type === 'shared' && (
                        <div className="flex items-center space-x-2">
                          <span className="text-xs text-gray-500 italic">Lecture seule</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Modal d'ajout/édition */}
      {isAddingEnquete && (
        <div 
          className="fixed top-0 left-0 right-0 bottom-0 w-screen h-screen bg-black/50 backdrop-blur-sm flex items-center justify-center z-[9999] p-4"
          style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh' }}
          onClick={resetForm}
        >
          <div 
            className="osint-card w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-white">
                {isEditingEnquete ? 'Modifier l\'enquête' : 'Nouvelle enquête'}
              </h3>
              <button
                onClick={resetForm}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Titre</label>
                  <input
                    type="text"
                    value={formData.titre}
                    onChange={(e) => setFormData(prev => ({ ...prev, titre: e.target.value }))}
                    className={`osint-input w-full px-3 py-2 rounded-lg text-white ${formErrors.titre ? 'border-red-500' : ''}`}
                    required
                  />
                  {formErrors.titre && (
                    <p className="text-red-400 text-xs mt-1">{formErrors.titre}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Client</label>
                  <select
                    value={formData.client}
                    onChange={(e) => setFormData(prev => ({ ...prev, client: e.target.value }))}
                    className={`osint-input w-full px-3 py-2 rounded-lg text-white ${formErrors.client ? 'border-red-500' : ''}`}
                    required
                    disabled={loadingClients}
                  >
                    <option value="">
                      {loadingClients ? 'Chargement...' : 'Sélectionnez un client'}
                    </option>
                    {clients.map(client => (
                      <option key={client.id} value={client.id}>
                        {client.nom} ({(client as any).type_client || (client as any).typeClient})
                      </option>
                    ))}
                  </select>
                  {formErrors.client && (
                    <p className="text-red-400 text-xs mt-1">{formErrors.client}</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  className={`osint-input w-full px-3 py-2 rounded-lg text-white ${formErrors.description ? 'border-red-500' : ''}`}
                  rows={3}
                  required
                />
                {formErrors.description && (
                  <p className="text-red-400 text-xs mt-1">{formErrors.description}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Objectifs de l'enquête</label>
                <textarea
                  value={formData.objectifs}
                  onChange={(e) => setFormData(prev => ({ ...prev, objectifs: e.target.value }))}
                  className={`osint-input w-full px-3 py-2 rounded-lg text-white ${formErrors.objectifs ? 'border-red-500' : ''}`}
                  rows={3}
                  placeholder="Décrivez les objectifs de l'enquête et la demande du client..."
                  required
                />
                {formErrors.objectifs && (
                  <p className="text-red-400 text-xs mt-1">{formErrors.objectifs}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Éléments fournis par le client</label>
                <textarea
                  value={formData.elementsFournis}
                  onChange={(e) => setFormData(prev => ({ ...prev, elementsFournis: e.target.value }))}
                  className={`osint-input w-full px-3 py-2 rounded-lg text-white ${formErrors.elementsFournis ? 'border-red-500' : ''}`}
                  rows={3}
                  placeholder="Listez les éléments, documents, informations fournis par le client..."
                  required
                />
                {formErrors.elementsFournis && (
                  <p className="text-red-400 text-xs mt-1">{formErrors.elementsFournis}</p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Statut</label>
                  <select
                    value={formData.statut}
                    onChange={(e) => setFormData(prev => ({ ...prev, statut: e.target.value as any }))}
                    className="osint-input w-full px-3 py-2 rounded-lg text-white"
                  >
                    <option value="en_attente">En attente</option>
                    <option value="en_cours">En cours</option>
                    <option value="terminee">Terminée</option>
                    <option value="archivee">Archivée</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Priorité</label>
                  <select
                    value={formData.priorite}
                    onChange={(e) => setFormData(prev => ({ ...prev, priorite: e.target.value as any }))}
                    className="osint-input w-full px-3 py-2 rounded-lg text-white"
                  >
                    <option value="basse">Basse</option>
                    <option value="moyenne">Moyenne</option>
                    <option value="haute">Haute</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Budget (€)</label>
                  <input
                    type="number"
                    value={formData.budget}
                    onChange={(e) => setFormData(prev => ({ ...prev, budget: parseInt(e.target.value) || 0 }))}
                    className={`osint-input w-full px-3 py-2 rounded-lg text-white ${formErrors.budget ? 'border-red-500' : ''}`}
                    min="0"
                  />
                  {formErrors.budget && (
                    <p className="text-red-400 text-xs mt-1">{formErrors.budget}</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Tags</label>
                <div className="flex items-center space-x-2 mb-2">
                  <input
                    type="text"
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    placeholder="Ajouter un tag..."
                    className="osint-input flex-1 px-3 py-2 rounded-lg text-white placeholder-gray-400"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                  />
                  <button
                    type="button"
                    onClick={addTag}
                    className="px-3 py-2 bg-osint-orange hover:bg-orange-600 rounded-lg text-white text-sm transition-colors"
                  >
                    Ajouter
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.tags.map(tag => (
                    <span key={tag} className="flex items-center space-x-1 px-2 py-1 bg-osint-orange/20 text-osint-orange text-sm rounded">
                      <span>{tag}</span>
                      <button
                        type="button"
                        onClick={() => removeTag(tag)}
                        className="hover:text-red-400"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              <div className="flex space-x-2 pt-4 sticky bottom-0 bg-osint-gray-800 -mx-6 px-6 pb-6 border-t border-osint-light-gray mt-6">
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 py-2 px-4 bg-osint-orange hover:bg-orange-600 disabled:opacity-50 rounded-lg text-white font-medium transition-colors"
                >
                  {loading ? 'Enregistrement...' : (isEditingEnquete ? 'Modifier' : 'Créer')}
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  className="py-2 px-4 bg-gray-600 hover:bg-gray-700 rounded-lg text-white font-medium transition-colors"
                >
                  Annuler
                </button>
              </div>
            </form>
            </div>
          </div>
        </div>
      )}

      {/* Interface détaillée */}
      {selectedEnqueteForDetail && (
        <EnqueteDetail 
          enqueteId={selectedEnqueteForDetail} 
          onClose={handleCloseDetail}
          onSave={(enqueteData) => updateEnquete(selectedEnqueteForDetail, enqueteData)}
        />
      )}

      {isExpertModalOpen && (
        <ExpertModal onClose={() => setIsExpertModalOpen(false)} />
      )}
    </div>
  );
};

export default EnqueteManager; 